import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { QUESTIONS } from "@/data/content";
import { Container, Section, Reveal } from "@/components/landing/shared";
import { usePrefersReducedMotion, EASE } from "@/lib/motion";
import { cn } from "@/lib/utils";

const TOTAL = QUESTIONS.length;
const QUESTION_MS = 3200;

const SLIDE_KEYS = QUESTIONS.map((_, i) => `q-${i + 1}`);
const pad = (n) => String(n).padStart(2, "0");
const wrapIndex = (n) => ((n % TOTAL) + TOTAL) % TOTAL;

const slideMotion = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
};

/** Auto-advances the deck unless paused or the user prefers reduced motion. */
const useAutoAdvance = ({ idx, paused, reduced, onAdvance }) => {
  useEffect(() => {
    if (reduced || paused) return undefined;
    const timer = setTimeout(onAdvance, QUESTION_MS);
    return () => clearTimeout(timer);
  }, [idx, paused, reduced, onAdvance]);
};

const DeckIntro = ({ idx, onPrev, onNext }) => (
  <div className="lg:col-span-4">
    <Reveal>
      <h2 className="font-display text-balance text-[1.9rem] leading-[1.06] tracking-[-0.02em] text-[color:var(--ink)] sm:text-[2.3rem] lg:text-[2.6rem]">
        What is really happening inside your organization?
      </h2>
      <p className="mt-5 text-[color:var(--muted-ink)]">
        Questions every leadership team should be able to answer.
      </p>
    </Reveal>
    <Reveal delay={2} className="mt-6 flex items-center gap-2">
      <DeckArrow label="Previous question" testId="question-deck-prev" onClick={onPrev} Icon={ChevronLeft} />
      <DeckArrow label="Next question" testId="question-deck-next" onClick={onNext} Icon={ChevronRight} />
      <span className="ml-3 font-mono text-[11px] uppercase tracking-[0.16em] text-[#7A8794]" data-testid="question-deck-counter">
        {`${pad(idx + 1)} / ${pad(QUESTIONS.length)}`}
      </span>
    </Reveal>
  </div>
);

const DeckArrow = ({ label, testId, onClick, Icon }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    data-testid={testId}
    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--hairline)] bg-white text-[color:var(--navy)] transition-colors hover:bg-[color:var(--navy)] hover:text-white"
  >
    <Icon className="h-4 w-4" />
  </button>
);

const QuestionSlide = ({ idx }) => (
  <motion.div key={SLIDE_KEYS[idx]} {...slideMotion} transition={{ duration: 0.45, ease: EASE }} data-testid="question-deck-slide">
    <div className="flex items-center gap-3">
      <span className="h-2 w-2 rounded-full bg-[color:var(--gold)]" aria-hidden="true" />
      <span className="font-mono text-[11px] tracking-[0.16em] text-[#7A8794]">QUESTION {pad(idx + 1)}</span>
    </div>
    <p className="font-display mt-4 text-balance text-[1.5rem] leading-[1.18] tracking-[-0.02em] text-[color:var(--ink)] sm:text-[1.85rem] lg:text-[2.1rem]" data-testid="question-deck-text">
      {QUESTIONS[idx]}
    </p>
  </motion.div>
);

const DeckProgress = ({ idx, paused, reduced }) => {
  const instant = paused || reduced;
  return (
    <div className="mt-6 flex items-center gap-1.5" aria-hidden="true">
      {SLIDE_KEYS.map((key, i) => (
        <span key={key} className="relative h-1 flex-1 overflow-hidden rounded-full bg-[color:var(--sand)]">
          {i < idx ? <span className="absolute inset-0 bg-[color:var(--navy)]" /> : null}
          {i === idx ? (
            <motion.span
              key={`${key}-${instant}`}
              className="absolute inset-y-0 left-0 bg-[color:var(--navy)]"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: instant ? 0.3 : QUESTION_MS / 1000, ease: "linear" }}
            />
          ) : null}
        </span>
      ))}
    </div>
  );
};

const DeckCard = ({ idx, paused, reduced }) => (
  <div
    className="relative flex min-h-[190px] flex-col justify-between overflow-hidden rounded-3xl border border-[color:var(--hairline)] bg-white p-6 shadow-[0_18px_50px_rgba(14,23,32,0.08)] sm:min-h-[210px] sm:p-8"
    data-testid="question-deck"
    aria-live="polite"
  >
    <AnimatePresence mode="wait">
      <QuestionSlide idx={idx} />
    </AnimatePresence>
    <DeckProgress idx={idx} paused={paused} reduced={reduced} />
  </div>
);

const chipClass = (active) =>
  cn(
    "inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-full border px-3 font-mono text-[11px] tracking-[0.12em] transition-colors duration-200",
    active
      ? "border-[color:var(--navy)] bg-[color:var(--navy)] text-white"
      : "border-[color:var(--hairline)] bg-white text-[color:var(--muted-ink)] hover:border-[#C9C2B5] hover:text-[color:var(--ink)]"
  );

const QuestionChips = ({ idx, onSelect }) => (
  <Reveal delay={1} className="mt-4 flex flex-wrap gap-2" data-testid="question-chips">
    {QUESTIONS.map((q, i) => (
      <button
        key={q}
        type="button"
        onClick={() => onSelect(i)}
        aria-label={`Show question ${i + 1}`}
        aria-pressed={idx === i}
        data-testid={`question-chip-${i + 1}`}
        className={chipClass(idx === i)}
      >
        {pad(i + 1)}
      </button>
    ))}
  </Reveal>
);

export const QuestionDeck = () => {
  const reduced = usePrefersReducedMotion();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = (n) => setIdx(wrapIndex(n));
  const advance = useCallback(() => setIdx((current) => wrapIndex(current + 1)), []);

  useAutoAdvance({ idx, paused, reduced, onAdvance: advance });

  return (
    <Section id="questions" navId="overview" className="bg-[color:var(--ivory)] hairline-top">
      <Container className="section-y">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
          <DeckIntro idx={idx} onPrev={() => go(idx - 1)} onNext={() => go(idx + 1)} />

          <div className="lg:col-span-8">
            <Reveal
              className="relative"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onFocusCapture={() => setPaused(true)}
              onBlurCapture={() => setPaused(false)}
            >
              <DeckCard idx={idx} paused={paused} reduced={reduced} />
            </Reveal>
            <QuestionChips idx={idx} onSelect={go} />
          </div>
        </div>
      </Container>
    </Section>
  );
};
